import type { CSSProperties } from 'react';
import type {
  Composition,
  CompositionCheckRequest,
  CompositionCheckResponse,
  CompositionUpdate,
  Correction,
  CorrectionAction,
  CorrectionActionRequest,
  LangPair,
  Page,
  Suggestion,
} from '@nelnela/contracts';
import type { Anchored, Span } from '@nelnela/corrections';
import { dropOverlaps, normalizeText } from '@nelnela/corrections';
import { isGeorgianChar } from './review-run';

/**
 * The journal's pure half (M6c step 7).
 *
 * `screens/Journal.tsx` is a `<textarea>` over a mirror, a caret-driven correction card, a
 * debounce and a keyboard-aware box. Only four of those genuinely need a browser — the
 * `selectionStart` read, the scroll sync, `visualViewport` and focus. Everything else is a
 * function of (text, spans, time), and it lives here so it can be held still by a test: the web
 * suite runs in node with no DOM, so a rule that stays in the component is a rule no test in this
 * repo can reach.
 *
 * **Nothing here re-implements span logic.** `locateCorrection` and `reanchor` are in
 * `@nelnela/corrections` — the worker module aliased into this bundle — and this file consumes
 * their output and reuses their `dropOverlaps`. The standing warning is M6a's dictation tint,
 * where a punctuation class copied into `Review.tsx` drifted by ONE code point and silently
 * killed the feature for a whole milestone; a second `reanchor` here would fail the same way, and
 * the symptom would be an underline under a word the learner did not write.
 */

// ---------------------------------------------------------------------------------------------
// when to spend a check
// ---------------------------------------------------------------------------------------------

/** Idle time before a draft is worth asking about (M6c step 7 — the numbers are the brief's). */
export const CHECK_IDLE_MS = 3_000;
/** How much has to have changed since the last check. A comma is not worth $0.012. */
export const CHECK_MIN_CHANGED_CHARS = 25;
/**
 * The client's copy of the per-composition day cap.
 *
 * The authority is `MAX_CHECKS_PER_COMPOSITION_DAY` in `packages/worker/src/lib/budget.ts`, which
 * refuses with a 429 the learner can read. This copy exists only so the client does not spend a
 * request that is guaranteed to be refused. Sharing the constant would mean aliasing the worker's
 * `budget.ts` into the web bundle — a module that imports the database layer — for one number, so
 * the copy is deliberate. If the two ever drift the SERVER wins, which is the safe direction: the
 * client asks less often than it may, never more.
 */
export const CHECK_CAP_PER_DAY = 20;

export interface CheckGate {
  /** ms since the last keystroke */
  idleMs: number;
  /** characters that differ from the text the last check was made against (`changedSince`) */
  changedChars: number;
  /** checks already spent on THIS composition today */
  checksToday: number;
  /** a check is already in flight — a second one would race it and pay twice */
  inFlight: boolean;
}

/**
 * All three conditions, and they are ANDed on purpose.
 *
 * A pure predicate rather than a chain of `setTimeout`s because the timer is the easy half and
 * the policy is the hard half: the screen polls this every half second with the clock it already
 * has, and a test can ask "does a 4-second pause after two characters spend money?" without
 * faking time. The alternative — a debounced callback holding the rule inside its closure — is
 * the shape this repo has already been bitten by, and it is untestable in a node suite.
 */
export function shouldCheck(g: CheckGate): boolean {
  if (g.inFlight) return false;
  if (g.idleMs < CHECK_IDLE_MS) return false;
  if (g.changedChars < CHECK_MIN_CHANGED_CHARS) return false;
  return g.checksToday < CHECK_CAP_PER_DAY;
}

/**
 * What to do after a check came back as a failure rather than as corrections.
 *
 * **The bug this replaces.** `runCheck`'s catch used to set a calm line and change nothing the
 * gate reads: `checkedText`, the check counter and `lastTypedAtRef` were all left as they were, so
 * the gate's next 500 ms tick found every condition still true and fired the same check again — at
 * 2 Hz, with no attempt counter and no ceiling. Offline that is an unbounded POST loop; on a
 * persistent `bad_reply` it is worse, because the server maps that to 502 *after* committing the
 * ledger row, so ~21 automatic retries reach `CONVERSATIONAL_USD_PER_LEARNER_DAY` and the learner
 * loses journal checks AND chat for the rest of the UTC day, in about two minutes, silently.
 *
 * The waits are long on purpose. The alternative — a short exponential backoff with no end —
 * still spends real money on a server that is answering 502 to every request, and the honest
 * thing to tell a learner after three failures is that the check is not coming and they may ask
 * again when they like. A refusal (429, 403) skips the ladder entirely: trying again cannot
 * change the answer, so slowing down would only be a slower way of being told the same thing.
 */
export const CHECK_BACKOFF_MS: readonly number[] = [15_000, 60_000];

export interface CheckFailure {
  /** consecutive failures INCLUDING the one that has just happened, so the first is 1 */
  failures: number;
  /** the refusal's HTTP status, or null when the request never reached a server (offline) */
  status: number | null;
}

export type CheckRetryPlan =
  /** try again, but not until this much time has passed */
  | { retry: 'wait'; waitMs: number }
  /** the automatic loop is done; only the learner asking may start another */
  | { retry: 'byHand' }
  /** an answer, not an accident — stop for this visit and say why */
  | { retry: 'never' };

export function checkRetryPlan(f: CheckFailure): CheckRetryPlan {
  if (f.status === 403 || f.status === 429) return { retry: 'never' };
  // The ladder's length IS the failure budget: run off the end and the loop stops.
  const waitMs = CHECK_BACKOFF_MS.at(Math.max(0, Math.round(f.failures) - 1));
  return waitMs === undefined ? { retry: 'byHand' } : { retry: 'wait', waitMs };
}

/**
 * How long to wait before trying a failed autosave again.
 *
 * Unlike the check, this never gives up: a PATCH carries no model call (`contracts:1811`), so a
 * retry costs nothing but a request, and what is at stake is the learner's own writing. So it
 * backs off to a ceiling and keeps knocking for as long as the editor is open. The ceiling exists
 * so that a tab left open on a dead connection is not a request every three seconds all afternoon.
 */
export const SAVE_RETRY_MS: readonly number[] = [3_000, 8_000, 20_000, 45_000, 60_000];

export function saveRetryDelayMs(failures: number): number {
  const i = Math.min(Math.max(1, Math.round(failures)), SAVE_RETRY_MS.length) - 1;
  return SAVE_RETRY_MS[i];
}

/**
 * How many characters differ between the checked text and the draft as it is now.
 *
 * Common prefix, common suffix, and the biggest thing left in the middle. It is not an edit
 * distance and does not try to be: a real diff over a paragraph on every keystroke buys nothing
 * the gate can use, and the number this returns is only ever compared against a threshold. It
 * over-counts a move (a paragraph dragged to the top reads as the whole paragraph changing),
 * which errs towards checking, and that is the right way for a *quality* feature to err.
 */
export function changedSince(checked: string, current: string): number {
  if (checked === current) return 0;
  const shorter = Math.min(checked.length, current.length);
  let prefix = 0;
  while (prefix < shorter && checked[prefix] === current[prefix]) prefix++;
  let suffix = 0;
  while (
    suffix < shorter - prefix &&
    checked[checked.length - 1 - suffix] === current[current.length - 1 - suffix]
  ) {
    suffix++;
  }
  return Math.max(checked.length, current.length) - prefix - suffix;
}

// ---------------------------------------------------------------------------------------------
// placements — what the mirror is allowed to draw
// ---------------------------------------------------------------------------------------------

/**
 * One correction, placed in the draft as it is NOW.
 *
 * `reanchor` answers "where is this correction's text today, if anywhere"; a placement answers
 * the next question, "and may it be drawn and tapped". They are separate because the second one
 * has answers the first cannot know about: a correction the learner has already ignored is
 * perfectly well anchored and must not open a card again.
 */
export interface Placement<T extends { id: string } = { id: string }> {
  correction: T;
  span: Span;
  /**
   * Drawn faded, with **no tap target**: the learner has dealt with this one already.
   *
   * Design `8d`'s caption says "stale corrections fade rather than mis-underline". Note what that
   * cannot mean: a correction `reanchor` could not place has no span, so there is nowhere honest
   * to fade it — drawing it at its stored offsets is precisely the mis-underline the caption is
   * against, and it is the one thing this screen must never do in a learner's own diary. So an
   * unplaceable correction is simply not drawn, and the faded state belongs to the corrections
   * that ARE placeable and are no longer live.
   */
  stale: boolean;
}

/**
 * `reanchor`'s output plus what the learner has done about each one → what to draw.
 *
 * - not `anchored` (its text is gone, moved ambiguously, or the fix was taken) → **not drawn**;
 * - `accepted` → not drawn. The DoD's own sentence: "Accept rewrites the span and the underline
 *   does not return." `reanchor` usually says `resolved` here on its own; this covers the frame
 *   between the splice and the next re-anchor, and the case where the learner then edits the
 *   accepted word into a third thing;
 * - `ignored` → drawn **stale**. "I know, leave it" is not "you were wrong to mark it", so the
 *   mark stays visible and quiet rather than vanishing, and it cannot be tapped into a second
 *   time. Removing it outright was the alternative and it reads as the app conceding the point;
 * - `edited` → still live. The learner said they would fix it themselves; until they have, the
 *   card is the thing that tells them what they were fixing.
 *
 * Overlaps are dropped left-to-right through `dropOverlaps` — the same function the server uses
 * on the model's reply — because two underlines on one word cannot both be tapped: the caret is
 * in both and the card would show whichever the array happened to list first.
 */
export function placementsFor<T extends { id: string; learnerAction?: CorrectionAction | null }>(
  anchored: readonly Anchored<T>[],
  acted: ReadonlyMap<string, CorrectionAction> = new Map(),
): Array<Placement<T>> {
  const placed: Array<Placement<T>> = [];
  for (const a of anchored) {
    if (a.state !== 'anchored' || !a.span) continue;
    const action = acted.get(a.correction.id) ?? a.correction.learnerAction ?? null;
    if (action === 'accepted') continue;
    placed.push({ correction: a.correction, span: a.span, stale: action === 'ignored' });
  }
  placed.sort((x, y) => x.span.charStart - y.span.charStart || x.span.charEnd - y.span.charEnd);
  return dropOverlaps(placed);
}

// ---------------------------------------------------------------------------------------------
// the mirror
// ---------------------------------------------------------------------------------------------

/** One span of the mirror: plain text, or text carrying a correction's underline. */
export interface MirrorRun {
  text: string;
  /** absent on a plain run; present on an underlined one, and the card's key */
  correctionId?: string;
  /** faded, no tap target (see `Placement.stale`) */
  stale?: boolean;
}

/**
 * The mirror's content: the whole draft, cut into runs.
 *
 * **The property that matters is that the runs rebuild the draft exactly** — every character,
 * once, in order. The mirror is what paints the underlines under a textarea that is rendering the
 * same string; a run boundary that loses or duplicates a character makes the two boxes wrap
 * differently, and from there every underline in the entry is under the wrong word. `culture.ts`
 * has the same property and the same test ("loses no character"), for the same reason.
 *
 * Defensive about its input on purpose, even though `placementsFor` already sorts and de-overlaps:
 * this is the last function before pixels, and the cost of a bad span reaching it is a diary
 * entry rendered with words missing. Out-of-range and inverted spans are clamped or dropped
 * rather than throwing — a broken correction must not take the editor down with it.
 */
export function mirrorRuns(text: string, placements: readonly Placement[]): MirrorRun[] {
  const spans = placements
    .map((p) => ({
      id: p.correction.id,
      stale: p.stale,
      charStart: Math.max(0, Math.min(text.length, p.span.charStart)),
      charEnd: Math.max(0, Math.min(text.length, p.span.charEnd)),
    }))
    .filter((s) => s.charEnd > s.charStart)
    .sort((a, b) => a.charStart - b.charStart || a.charEnd - b.charEnd);

  const runs: MirrorRun[] = [];
  let at = 0;
  for (const s of spans) {
    if (s.charStart < at) continue; // overlaps one already drawn — first wins
    if (s.charStart > at) runs.push({ text: text.slice(at, s.charStart) });
    runs.push({
      text: text.slice(s.charStart, s.charEnd),
      correctionId: s.id,
      ...(s.stale ? { stale: true } : {}),
    });
    at = s.charEnd;
  }
  if (at < text.length) runs.push({ text: text.slice(at) });
  return runs;
}

/**
 * The correction the card should show for a caret at `caretIndex`, or null.
 *
 * Design `8d` docks the card above the suggestion strip rather than anchoring it to the word, and
 * this is what that buys: the card is opened by the caret, not by a hit test on the mirror. That
 * is more accurate than testing a tap against an underline three pixels tall, it works from a
 * hardware keyboard and from arrow keys, and it means the mirror needs no pointer events at all —
 * which is what lets it sit under the textarea without stealing a single touch.
 *
 * **The end index counts as inside, and the start does too.** A caret is between characters, so
 * the honest reading of `selectionStart === charEnd` is "immediately after the last letter of the
 * underlined word" — which is exactly where a thumb lands when someone taps the end of a word,
 * and where the caret goes when they finish typing it. The half-open alternative `[start, end)` is
 * the arithmetically tidier one and was rejected: it makes the trailing edge of every underlined
 * word dead, and a learner who taps there twice concludes the underline is not tappable at all.
 * The cost is that two spans that touch (`A.end === B.start`) both contain that caret; the
 * leftmost wins, which is the word the learner was just typing.
 *
 * Stale placements are skipped rather than returned-and-ignored, so "no tap target" is a fact
 * about this function rather than a rule the component has to remember.
 */
export function correctionAtCaret<T extends { id: string }>(
  placements: readonly Placement<T>[],
  caretIndex: number,
): Placement<T> | null {
  let best: Placement<T> | null = null;
  for (const p of placements) {
    if (p.stale) continue;
    if (caretIndex < p.span.charStart || caretIndex > p.span.charEnd) continue;
    if (!best || p.span.charStart < best.span.charStart) best = p;
  }
  return best;
}

// ---------------------------------------------------------------------------------------------
// edits the buttons make
// ---------------------------------------------------------------------------------------------

/** A draft and where the caret should be left in it. */
export interface Edit {
  text: string;
  caret: number;
}

/**
 * The editor's one boundary: what the textarea holds → the string everything else is about.
 *
 * **The bug this exists to close.** A correction's offsets are indices into `NFC(text)` — the
 * worker normalises before it locates a span, and `PATCH /compositions/:id` normalises before it
 * STORES the draft — while the editor held whatever the textarea had. The two agree for Georgian,
 * which has no combining forms, and come apart the moment anything else gets into the entry: a
 * pasted decomposed accent, a Hangul jamo, a keyboard that produces `e` + U+0301. Every such pair
 * before a span shifts the mark one place left, so the mirror underlined a space and three letters
 * of the word beside the one the card was explaining, `correctionAtCaret` opened cards for a caret
 * that was not under the drawn mark, and Accept spliced the fix at the wrong index and turned the
 * learner's own sentence into nonsense.
 *
 * Normalising at the boundary rather than re-anchoring against the raw text, deliberately: the
 * server stores the normalised form, so this is the only choice that leaves the client's draft
 * byte-identical to the server's copy — one string, one set of indices, everywhere. Re-anchoring
 * would have meant a second coordinate system and a translation between them on every keystroke,
 * every mirror run, every splice and every caret read, which is more places to be wrong than the
 * bug had.
 *
 * The caret comes along because it is an index into the string it was read from: composing four
 * characters into three in front of it moves it. Measuring the prefix is exact except when the
 * caret sits BETWEEN a base character and its combining mark, where it lands after the composed
 * pair — the only honest place left, since the position it named no longer exists.
 */
export function normalizeDraft(raw: string, caret: number): Edit {
  const text = normalizeText(raw);
  const at = Math.max(0, Math.min(raw.length, caret));
  // The overwhelmingly common path — every keystroke of Georgian, of Latin, of punctuation — is
  // the identity, and it costs one string comparison to say so.
  if (text === raw) return { text, caret: at };
  return { text, caret: Math.min(text.length, normalizeText(raw.slice(0, at)).length) };
}

/**
 * Accept: the suggested form replaces the span, and the caret is left at the end of what was
 * written — where it would be if the learner had typed the fix themselves, which is the point.
 *
 * The splice is done on the string rather than through `document.execCommand('insertText')`, so
 * the native undo stack loses this edit. That is the known cost and it is accepted: `execCommand`
 * is deprecated, is a no-op in some Android WebViews, and needs the textarea focused and selected
 * first — three ways for Accept to silently do nothing, against one way for Ctrl-Z to undo one
 * step too many.
 */
export function applyAccept(text: string, span: Span, suggested: string): Edit {
  const start = Math.max(0, Math.min(text.length, span.charStart));
  const end = Math.max(start, Math.min(text.length, span.charEnd));
  // The model's string is normalised HERE because nothing normalises it earlier: the check route
  // stores the reply's `suggested` as it arrived (`validateCorrectReply` trims it and checks its
  // script, and no more). Splicing a decomposed form into the draft would put the editor back out
  // of step with the string the PATCH stores — the bug `normalizeDraft` exists to close — one
  // Accept after this function had just put it in step. The caret is measured against the
  // normalised form for the same reason.
  const insert = normalizeText(suggested);
  return {
    text: text.slice(0, start) + insert + text.slice(end),
    caret: start + insert.length,
  };
}

/**
 * A suggestion chip, inserted at the caret with the spaces a sentence needs.
 *
 * The chip is a phrase, not a word, and the learner taps it mid-sentence as often as at the end.
 * Inserting it raw produced `ხვალადრე ავდგები` — two words fused — which then fails to tag, so
 * the item never reaches an event. A space is added on whichever side is currently up against a
 * non-space character, and never against the ends of the draft.
 */
export function insertSuggestion(text: string, caret: number, chip: string): Edit {
  const at = Math.max(0, Math.min(text.length, caret));
  const before = text.slice(0, at);
  const after = text.slice(at);
  const lead = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
  const trail = after.length > 0 && !/^\s/.test(after) ? ' ' : '';
  // Normalised for the reason `applyAccept` gives: a suggestion is the model's string and nothing
  // upstream composes it, and the draft this returns is about to be measured in NFC indices.
  const phrase = normalizeText(chip);
  const insert = lead + phrase + trail;
  // The caret lands after the chip and BEFORE the trailing space: the learner is mid-sentence and
  // the next thing they type belongs after the phrase, not after a space they did not ask for.
  return { text: before + insert + after, caret: at + lead.length + phrase.length };
}

// ---------------------------------------------------------------------------------------------
// what the chrome says
// ---------------------------------------------------------------------------------------------

/** The header's right-hand word. `checking…` is M6c's addition; `8d` draws only `saved`. */
export type JournalStatus = 'saved' | 'saving…' | 'checking…' | 'not saved';

/**
 * Which one word to show.
 *
 * `checking…` outranks `saving…` because a Sonnet call takes seconds and an autosave takes
 * milliseconds: the check is the thing the learner is actually waiting for, and silence during it
 * reads as a hang. Both are dropped the moment neither is in flight — a status that lingers is a
 * status nobody believes.
 *
 * **`not saved` outranks everything, and it exists because the header used to lie.** A failed
 * autosave only `console.warn`ed, left `savedTextRef` alone and set `saving` back to false — so
 * this function returned `saved` for a paragraph that was on no server, in the one place on the
 * screen the learner can look. It is the only word here that is a warning rather than a progress
 * note, which is why it wins. The exception is a retry actually in flight: `saving…` is the truer
 * word for those seconds, and a header frozen on `not saved` while a request is out would make a
 * recovering save look like a dead one.
 */
export function statusWord(s: {
  saving: boolean;
  checking: boolean;
  failed: boolean;
}): JournalStatus {
  if (s.failed && !s.saving) return 'not saved';
  if (s.checking) return 'checking…';
  if (s.saving) return 'saving…';
  return 'saved';
}

/**
 * What a failed LOAD of one entry may honestly say.
 *
 * **The bug:** the editor's only test was `!entryQ.data`, and React Query is configured
 * `retry: false` (`App.tsx:46`), so one offline second or a single 500 resolved the query to no
 * data and the screen told the learner _"That entry isn't here. It may have been deleted."_ —
 * about writing that was sitting safely on the server. It is the worst copy in the milestone, and
 * the list view two screens up already gets the same failure right. So the 404 keeps the deletion
 * line, because a 404 really may have been deleted, and everything else gets the list's own
 * connection sentence and a button.
 */
export interface LoadProblem {
  line: string;
  /** may asking again plausibly change the answer — false for a 404, which will stay a 404 */
  retryable: boolean;
}

export function entryLoadProblem(status: number | null): LoadProblem {
  if (status === 404) {
    return { line: 'That entry isn’t here. It may have been deleted.', retryable: false };
  }
  return { line: 'Your entry will be here when the connection is.', retryable: true };
}

/** How long a list row's preview may be before it is cut. */
export const PREVIEW_MAX = 60;

/**
 * The list row's headline: the title the learner gave it, else the first line they wrote, else
 * `Untitled`.
 *
 * The first line rather than the first N characters of the whole draft: a diary's first line IS
 * its title most of the time, and a preview that runs the first two sentences together reads as
 * mangled rather than as a summary. Blank lines are skipped, so an entry that opens with a
 * newline is not titled with nothing.
 */
export function entryTitle(c: { title?: string | null; currentText?: string | null }): string {
  const title = (c.title ?? '').trim();
  if (title) return title;
  for (const line of (c.currentText ?? '').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    return trimmed.length > PREVIEW_MAX ? `${trimmed.slice(0, PREVIEW_MAX).trimEnd()}…` : trimmed;
  }
  return 'Untitled';
}

/**
 * "edited 2h ago", for the list row.
 *
 * Coarse and locale-free on purpose. The exact minute of an autosave is not information — the
 * question the row answers is "is this the one I was writing yesterday" — and a locale-formatted
 * date would make this function's test depend on the machine's timezone, which is the kind of
 * flake that gets a test deleted rather than fixed.
 */
export function editedAgo(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return 'recently';
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/**
 * Is this draft plainly not in Georgian?
 *
 * `Review.tsx` asks the same question of the FIRST keystroke, because an exercise answer is one
 * word and the alphabet is settled by its first letter. A diary is not: it will carry a place
 * name, a brand, a date, an English word the learner does not know yet — so a first-keystroke
 * trigger here would fire on someone who wrote "20 August —" and then switched keyboards, which
 * is an interruption for nothing.
 *
 * So it asks about the draft instead: enough letters to be a sentence, and not one of them
 * Georgian. `isGeorgianChar` is imported from `review-run.ts` rather than copied — it is the same
 * question, and the two ranges (Georgian plus Mtavruli) must not drift apart.
 */
export const NOT_GEORGIAN_MIN_LETTERS = 8;

export function looksNotGeorgian(text: string): boolean {
  let letters = 0;
  for (const ch of text) {
    if (isGeorgianChar(ch)) return false;
    if (/\p{L}/u.test(ch)) letters++;
  }
  return letters >= NOT_GEORGIAN_MIN_LETTERS;
}

// ---------------------------------------------------------------------------------------------
// the one style object
// ---------------------------------------------------------------------------------------------

/**
 * The box the textarea and the mirror BOTH wear.
 *
 * One function, called once per render, spread into both nodes. This is not tidiness: the mirror
 * only paints under the right words while the two boxes wrap identically, and a one-pixel
 * difference in font size, line height, padding, letter spacing or word breaking makes every
 * underline in a long entry drift a little further from the word it belongs to. Two style objects
 * that "say the same thing" is exactly how that difference gets introduced six months from now,
 * so there is only one, and a test asserts both nodes carry the same
 * `--nn-transcript-size` — and, since the scrollbar bug below, that the two nodes' rendered styles
 * differ ONLY in ink (colour, caret, selection, overflow) and in nothing that can move a character.
 *
 * The size is delivered as the custom property rather than as a number, because that is how the
 * learner's 16–18 px setting reaches every other reading surface in the app
 * (`StoryReader.tsx`, `Transcript.tsx`) and `READING_SURFACE_CSS` already knows the name.
 * 17/1.66 in Noto Sans Georgian is the reading surface's own type (reading-surface-spec §1) —
 * ⟂ `8d` draws the editor at 15 px and loses to the spec and to the setting.
 */
export function editorSurfaceStyle(fontSizePx: number): CSSProperties {
  // The `as CSSProperties` on the literal is `StoryReader.tsx`'s own idiom: React passes any
  // `--*` key straight through to the DOM as a custom property, and TypeScript has no key for it.
  return {
    position: 'absolute',
    inset: 0,
    margin: 0,
    padding: '10px 20px 28px',
    border: 0,
    boxSizing: 'border-box',
    fontFamily: "'Noto Sans Georgian', sans-serif",
    fontSize: 'var(--nn-transcript-size)',
    lineHeight: 1.66,
    letterSpacing: 'normal',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    tabSize: 4,
    textAlign: 'start',
    // **The bug this line fixes.** The textarea is `overflow-y: auto` and the mirror is
    // `overflow: hidden`, so on every platform with classic (space-taking) scrollbars — Windows
    // and Linux, and macOS set to "Show scroll bars: Always" — an entry taller than the box grew a
    // ~15 px scrollbar inside the TEXTAREA's padding box and narrowed its content by that much
    // while the mirror kept the full width. The two wrapped at different words from there down,
    // and every underline below that line sat under different Georgian than the textarea was
    // showing: the exact drift this whole function exists to prevent, made by a scrollbar instead
    // of by a declaration, and invisible on macOS and on both mobile browsers, which overlay it.
    //
    // Hiding it here rather than reserving a gutter, and here rather than in `JOURNAL_CSS`:
    // `scrollbar-gutter: stable` would also match the two boxes, but only where it is supported
    // and only while nothing else sets `scrollbar-width` — and a rule in the stylesheet reaches
    // one node by class, which is how the two came apart in the first place. From this object it
    // cannot reach one and miss the other. `.nn-journal-strip` already hides its scrollbar the
    // same way; the `::-webkit-scrollbar` half, which cannot be written inline, is in
    // `JOURNAL_CSS` beside it.
    scrollbarWidth: 'none',
    '--nn-transcript-size': `${fontSizePx}px`,
  } as CSSProperties;
}

// ---------------------------------------------------------------------------------------------
// the API surface this screen needs
// ---------------------------------------------------------------------------------------------

/**
 * The six methods `lib/api.ts` gains for the journal (M6c step 6's routes).
 *
 * `api.ts` belongs to the integrator, not to this step, so the screen is written against this
 * interface and the integrator adds methods matching it. Declared here rather than inside the
 * screen so the exact shape is one grep away from the routes it mirrors, and so a later drift
 * between the two shows up as a type error in one place.
 */
export interface JournalApi {
  compositionList(q?: { limit?: number; cursor?: string }): Promise<Page<Composition>>;
  compositionCreate(body: LangPair & { title?: string }): Promise<Composition>;
  compositionGet(
    compositionId: string,
  ): Promise<Composition & { corrections: Correction[]; suggestions: Suggestion[] }>;
  /** `init.keepalive` is what makes the `pagehide` autosave survive the page going away; an
   *  ordinary fetch fired from that handler is not guaranteed to be delivered. */
  compositionUpdate(
    compositionId: string,
    body: CompositionUpdate,
    init?: { keepalive?: boolean },
  ): Promise<Composition>;
  compositionCheck(
    compositionId: string,
    body: CompositionCheckRequest,
  ): Promise<CompositionCheckResponse>;
  correctionAct(correctionId: string, body: CorrectionActionRequest): Promise<void>;
}
